"use client"

import * as React from "react"
import { CheckIcon, ChevronDownIcon } from "lucide-react"
import {
  Button as ButtonPrimitive,
  composeRenderProps,
  ListBox as ListBoxPrimitive,
  ListBoxItem as ListBoxItemPrimitive,
  Popover as PopoverPrimitive,
  Select as SelectPrimitive,
  SelectValue as SelectValuePrimitive,
  type ListBoxItemProps as ListBoxItemPrimitiveProps,
  type SelectProps as SelectPrimitiveProps,
} from "react-aria-components"

import { cn } from "@/lib/utils"

/**
 * A single-select dropdown built on react-aria-components — NOT a native `<select>`.
 *
 * Why not native? A native `<select>`'s option list is drawn by the OS, so it can't be dark-themed and ignores our
 * tokens (the reported bug: a light OS dropdown on a dark drawer). This renders the list ourselves via `Popover` +
 * `ListBox`, so it inherits `--popover` and matches every other overlay in the app.
 *
 * The trigger shares the exact field skin + focus treatment as `Input`/`Textarea` (border-border, one flush
 * brand-`--primary` focus line, `dark:bg-input/30`). Keep the trigger classes in lockstep with input.tsx.
 *
 * Usage — controlled by `selectedKey`/`onSelectionChange` (react-aria `Key` = string | number):
 *   <Select aria-label="Section" placeholder="—" selectedKey={id} onSelectionChange={(k) => …}>
 *     <SelectTrigger />
 *     <SelectContent>
 *       <SelectItem id={1}>First</SelectItem>
 *     </SelectContent>
 *   </Select>
 * Add a react-aria `Label` as the first child for a visible, associated label.
 */
function Select<T extends object>({
  className,
  ...props
}: SelectPrimitiveProps<T>) {
  return (
    <SelectPrimitive
      data-slot="select"
      className={composeRenderProps(className, (className) =>
        cn("group/select flex flex-col gap-1", className)
      )}
      {...props}
    />
  )
}

/**
 * `renderValue` replaces the default text value (an icon-only trigger, e.g. the grants tree's level picker — keep the
 * accessible name by giving the icon `role="img"` + `aria-label`); `chevron={false}` drops the caret for such compact
 * triggers. Both default to the text + caret shape every other select uses.
 */
function SelectTrigger({
  className,
  children,
  renderValue,
  chevron = true,
  ref,
  ...props
}: React.ComponentProps<typeof ButtonPrimitive> & {
  renderValue?: React.ComponentProps<typeof SelectValuePrimitive>["children"]
  chevron?: boolean
}) {
  return (
    <ButtonPrimitive
      // The ref is forwarded EXPLICITLY (React 19 passes it as a prop): a `TooltipTrigger` around this trigger wraps it
      // in react-aria's Focusable, which must reach the DOM button through us.
      ref={ref}
      data-slot="select-trigger"
      className={composeRenderProps(className, (className) =>
        cn(
          "flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-border bg-transparent px-2.5 py-1 text-base transition-colors outline-none data-[focus-visible]:border-primary data-[focus-visible]:ring-1 data-[focus-visible]:ring-primary data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:bg-input/50 data-disabled:opacity-50 data-[invalid]:border-destructive data-[invalid]:ring-3 data-[invalid]:ring-destructive/20 md:text-sm dark:bg-input/30 dark:data-disabled:bg-input/80 dark:data-[invalid]:border-destructive/50 dark:data-[invalid]:ring-destructive/40",
          className
        )
      )}
      {...props}
    >
      {composeRenderProps(children, (children) => (
        <>
          <SelectValuePrimitive
            data-slot="select-value"
            className={cn("min-w-0 truncate text-left data-[placeholder]:text-muted-foreground", renderValue ? "flex items-center" : "flex-1")}
          >
            {renderValue}
          </SelectValuePrimitive>
          {children}
          {chevron ? <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0 opacity-60" /> : null}
        </>
      ))}
    </ButtonPrimitive>
  )
}

/**
 * `popoverClassName` reshapes the overlay itself (default: as wide as the trigger) — an icon-only trigger needs a
 * `w-auto` popover, a horizontal bar needs `orientation="horizontal"` on the list (passed through to the ListBox).
 */
function SelectContent({
  className,
  popoverClassName,
  children,
  ...props
}: React.ComponentProps<typeof ListBoxPrimitive> & {
  className?: string
  popoverClassName?: string
}) {
  return (
    <PopoverPrimitive
      data-slot="select-content"
      offset={4}
      className={cn(
        "z-50 max-h-[min(24rem,var(--visual-viewport-height))] w-(--trigger-width) min-w-32 origin-(--trigger-anchor-point) overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none data-entering:animate-in data-entering:fade-in-0 data-entering:zoom-in-95 data-exiting:animate-out data-exiting:fade-out-0 data-exiting:zoom-out-95 data-[placement=bottom]:slide-in-from-top-2 data-[placement=top]:slide-in-from-bottom-2",
        popoverClassName
      )}
    >
      <ListBoxPrimitive
        data-slot="select-listbox"
        className={cn("max-h-[inherit] overflow-auto outline-hidden", className)}
        {...props}
      >
        {children}
      </ListBoxPrimitive>
    </PopoverPrimitive>
  )
}

/**
 * `indicator="none"` renders the children verbatim — no truncating wrapper, no check mark — for glyph items whose
 * selected state is their own background (the grants tree's icon bar). The default keeps the text + check shape.
 */
function SelectItem({
  className,
  children,
  indicator = "check",
  ...props
}: ListBoxItemPrimitiveProps & { indicator?: "check" | "none" }) {
  return (
    <ListBoxItemPrimitive
      data-slot="select-item"
      textValue={typeof children === "string" ? children : props.textValue}
      className={composeRenderProps(className, (className) =>
        cn(
          "relative flex cursor-default items-center gap-2 rounded-md py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-focused:bg-accent data-focused:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
          indicator === "none" && "pr-2 data-selected:bg-accent data-selected:text-accent-foreground",
          className
        )
      )}
      {...props}
    >
      {indicator === "none"
        ? children
        : composeRenderProps(children, (children, { isSelected }) => (
            <>
              <span className="min-w-0 flex-1 truncate">{children}</span>
              {isSelected ? (
                <CheckIcon aria-hidden="true" className="absolute right-2 size-4 shrink-0" />
              ) : null}
            </>
          ))}
    </ListBoxItemPrimitive>
  )
}

export { Select, SelectTrigger, SelectContent, SelectItem }
